import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './utils/queryClient';
import Layout from './components/Layout/Layout';
import { CategoriesProvider } from './contexts/CategoriesContext';
import { UIProvider } from './contexts/UIContext';
import Home from './pages/Home/Home';
import CategoryPage from './pages/CategoryPage/CategoryPage';
import CompanyPage from './pages/CompanyPage/CompanyPage';
import NotFound from './pages/NotFound/NotFound';

// Test Pages
import CardPage from './test-pages/CardPage/CardPage';
import CardGroupPage from './test-pages/CardGroupPage/CardGroupPage';
import VideoPreviewPage from './test-pages/VideoPreviewPage/VideoPreviewPage';
import NavBarPage from './test-pages/NavBarPage/NavBarPage';
import EmptyStatePage from './test-pages/EmptyStatePage/EmptyStatePage';
import SecondaryCategoriesPage from './test-pages/SecondaryCategoriesPage/SecondaryCategoriesPage';
import PrimaryCategoriesPage from './test-pages/PrimaryCategoriesPage/PrimaryCategoriesPage';
import SkeletonCardPage from './test-pages/SkeletonCardPage/SkeletonCardPage';
import HeroSectionPage from './test-pages/HeroSectionPage/HeroSectionPage';
import CardCompanyPage from './test-pages/CardCompanyPage/CardCompanyPage';
import CardOverlayPage from './test-pages/CardOverlayPage/CardOverlayPage';
import DiscoverSubCategoriesPage from './test-pages/DiscoverSubCategoriesPage/DiscoverSubCategoriesPage';
import CompanyPagePage from './test-pages/CompanyPagePage/CompanyPagePage';
import MostPopularCardPage from './test-pages/MostPopularCardPage/MostPopularCardPage';
import DiscoverSectionPage from './test-pages/DiscoverSectionPage/DiscoverSectionPage';
import HomePage from './test-pages/HomePage/HomePage';
import TrendingSubCategoriesPage from './test-pages/TrendingSubCategoriesPage/TrendingSubCategoriesPage';
import JustInSubCategoriesPage from './test-pages/JustInSubCategoriesPage/JustInSubCategoriesPage';
import LaunchedSubCategoriesPage from './test-pages/LaunchedSubCategoriesPage/LaunchedSubCategoriesPage';
import ContextTester from './test-pages/ContextTester';
import FooterPage from './test-pages/FooterPage/FooterPage';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <UIProvider>
          <CategoriesProvider>
            <Layout>
            <Routes>
              {/* Main Application Routes */}
              <Route path="/" element={<Home />} />
              
              <Route 
                path="/discover/:category?" 
                element={
                  <CategoryPage 
                    primaryCategory="Discover"
                    tickerTextId={1}
                  />
                } 
              />

              <Route 
                path="/trending/:category?" 
                element={
                  <CategoryPage 
                    primaryCategory="Trending"
                    tickerTextId={3}
                  />
                } 
              />

              <Route 
                path="/just_in/:category?" 
                element={
                  <CategoryPage 
                    primaryCategory="Just In"
                    tickerTextId={4}
                  />
                } 
              />

              <Route 
                path="/launched/:category?" 
                element={
                  <CategoryPage 
                    primaryCategory="Launched"
                    tickerTextId={5}
                  />
                } 
              />

              <Route 
                path="/company/:companySlug" 
                element={<CompanyPage />} 
              />

              {/* Test Pages Routes */}
              <Route path="/test/card" element={<CardPage />} />
              <Route path="/test/cardgroup" element={<CardGroupPage />} />
              <Route path="/test/videopreview" element={<VideoPreviewPage />} />
              <Route path="/test/navbar" element={<NavBarPage />} />
              <Route path="/test/emptystate" element={<EmptyStatePage />} />
              <Route path="/test/secondarycategories" element={<SecondaryCategoriesPage />} />
              <Route path="/test/primarycategories" element={<PrimaryCategoriesPage />} />
              <Route path="/test/skeletoncard" element={<SkeletonCardPage />} />
              <Route path="/test/herosection" element={<HeroSectionPage />} />
              <Route path="/test/cardcompany" element={<CardCompanyPage />} />
              <Route path="/test/cardoverlay" element={<CardOverlayPage />} />
              <Route path="/test/discoversubcategories" element={<DiscoverSubCategoriesPage />} />
              <Route path="/test/companypage" element={<CompanyPagePage />} />
              <Route path="/test/mostpopularcard" element={<MostPopularCardPage />} />
              <Route path="/test/discoversection" element={<DiscoverSectionPage />} />
              <Route path="/test/home" element={<HomePage />} />
              <Route path="/test/trendingsubcategories" element={<TrendingSubCategoriesPage />} />
              <Route path="/test/justinsubcategories" element={<JustInSubCategoriesPage />} />
              <Route path="/test/launchedsubcategories" element={<LaunchedSubCategoriesPage />} />
              <Route path="/test/contexts" element={<ContextTester />} />
              <Route path="/test/footer" element={<FooterPage />} />

              {/* 404 and Catch All */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
            </Layout>
          </CategoriesProvider>
        </UIProvider>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;